import { useParams } from 'react-router-dom';

/* Which paths the poster funnel answers on.
 *
 * `/giveaway-offer` used to be the only one. An admin now picks others —
 * `/token2049-offer` for an event, more later — and the client learns them at
 * runtime from `GET /giveaway/offer/pages`, so a new poster path needs no client
 * deploy. `GiveawayOfferGate` asks this file whether a path it was handed is one
 * of them.
 *
 * ONLY THE URL CHANGES. `'giveaway-offer'` is also the `signupSource` the pages
 * send — the server's entitlement keys off that exact string — and the prefix of
 * every `giveaway_offer.*` analytics event. Neither follows the slug; the slug
 * rides along as an `offerSlug` property instead.
 *
 * `/giveaway-offer` NEVER DEPENDS ON THE LOOKUP. It keeps its own static routes
 * in App.tsx, and a failed lookup still answers with it, so the posters already
 * printed work whatever the endpoint is doing.
 */

/** The funnel's original path, and the one slug that is always served. */
export const DEFAULT_OFFER_SLUG = 'giveaway-offer';

export type OfferPage = 'offer' | 'confirmation' | 'subscribed';

/* The server's rule for a slug, repeated so a malformed path is turned away
 * without a request: lower-case words joined by single hyphens, 3–40 chars. */
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const isOfferSlug = (slug: string) =>
  slug.length >= 3 && slug.length <= 40 && SLUG_RE.test(slug);

/** Which page a URL is ASKING for, judged on its shape alone — a well-formed
 * slug followed by nothing, `confirmation` or `subscribed`. Says nothing about
 * whether the slug is live; `offerPageFor` does that. */
export const offerPageShape = (
  offerSlug = '',
  rest = ''
): OfferPage | null => {
  if (!isOfferSlug(offerSlug.toLowerCase())) return null;
  /* `/:offerSlug/*` hands over `confirmation/` for a trailing slash. */
  const tail = rest.replace(/\/+$/, '').toLowerCase();
  if (tail === '') return 'offer';
  if (tail === 'confirmation' || tail === 'subscribed') return tail;
  return null;
};

/** The page to render, or null to fall through — the slug must also be one of
 * `slugs`. Matched lower-cased, as React Router matches the static routes. */
export const offerPageFor = (
  offerSlug: string | undefined,
  rest: string | undefined,
  slugs: readonly string[]
): OfferPage | null => {
  const page = offerPageShape(offerSlug, rest);
  return page && slugs.includes((offerSlug ?? '').toLowerCase()) ? page : null;
};

/** The slugs in an answer from the API, or null when it is not one. Always
 * includes `giveaway-offer`; anything that is not a slug is dropped. */
export const offerSlugsFrom = (body: unknown): readonly string[] | null => {
  const slugs = (body as { slugs?: unknown } | null | undefined)?.slugs;
  if (!Array.isArray(slugs)) return null;
  const valid = slugs
    .filter((s): s is string => typeof s === 'string')
    .map((s) => s.trim().toLowerCase())
    .filter(isOfferSlug);
  return Array.from(new Set([DEFAULT_OFFER_SLUG, ...valid]));
};

/* Loaded at most once per page load, and kept two ways.
 *
 * In memory for the rest of the page load, so stepping from the offer to its
 * confirmation does not ask again. In sessionStorage for five minutes, so a
 * RELOAD — or the return from a Stripe redirect, which is a full page load onto
 * `/<slug>/confirmation` — renders at once instead of behind an empty page.
 *
 * THE STORED COPY MAY SAY YES, NEVER NO. A copy taken before an admin added a
 * slug would otherwise send that slug's visitors to the homepage for five
 * minutes — at a live event, exactly the people who scanned before the
 * promotion was switched on and are scanning again. So a slug missing from the
 * copy is always asked about on the network.
 *
 * A FAILED LOOKUP IS NOT REMEMBERED. It answers with the funnel's own path for
 * now, and the next page that needs the list asks again. */
const STORE_KEY = 'giveawayOfferPages';
const STORE_MAX_AGE_MS = 5 * 60 * 1000;
const LOOKUP_TIMEOUT_MS = 8000;
const ON_FAILURE: readonly string[] = [DEFAULT_OFFER_SLUG];

let loaded: readonly string[] | null = null;
let pending: Promise<readonly string[]> | null = null;

/** The list as this page load received it, or null before it has. */
export const loadedOfferSlugs = () => loaded;

/** The copy a previous page load left, if it is under five minutes old. */
export const storedOfferSlugs = (): readonly string[] | null => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { at?: unknown; slugs?: unknown } | null;
    const age = Date.now() - Number(stored?.at);
    return age >= 0 && age < STORE_MAX_AGE_MS ? offerSlugsFrom(stored) : null;
  } catch {
    return null;
  }
};

/* No credentials: the list is the same for everyone (CardCatalog reads its
 * public list the same way). The timeout is there because the gate shows an
 * empty page until this answers — a hung request has to end, in the fallback,
 * rather than leave a scanned poster on a blank screen. */
const fetchOfferSlugs = async (): Promise<readonly string[]> => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (!base) throw new Error('REACT_APP_API_URL is not set');

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/giveaway/offer/pages`, {
      credentials: 'omit',
      signal: ctrl.signal
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const slugs = offerSlugsFrom(await res.json());
    if (!slugs) throw new Error('no slugs in the response');
    return slugs;
  } finally {
    clearTimeout(timer);
  }
};

/** Ask the API, once per page load. Never rejects: a failure answers with
 * `giveaway-offer` alone. */
export const loadOfferSlugs = (): Promise<readonly string[]> => {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    pending = fetchOfferSlugs().then(
      (slugs) => {
        loaded = slugs;
        try {
          sessionStorage.setItem(
            STORE_KEY,
            JSON.stringify({ at: Date.now(), slugs })
          );
        } catch {
          /* Private browsing, or a full quota: a reload simply asks again. */
        }
        return slugs;
      },
      () => {
        pending = null;
        return ON_FAILURE;
      }
    );
  }
  return pending;
};

/** The slug this page was opened on — the `:offerSlug` param, lower-cased, or
 * `giveaway-offer` on the funnel's own static routes. Builds the funnel's links
 * and tags its analytics; it is never sent as `signupSource`. */
export const useOfferSlug = () => {
  const { offerSlug } = useParams();
  return offerSlug ? offerSlug.toLowerCase() : DEFAULT_OFFER_SLUG;
};
